import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Wand2, Loader2, FileText, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ToolForm = ({ tool, common, setCommon, onResult }) => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => setValues({}), [tool.id]);

  const set = (name, val) => setValues((v) => ({ ...v, [name]: val }));
  const setC = (name, val) => setCommon((c) => ({ ...c, [name]: val }));
  const loadPreset = () => {
    setValues(tool.preset);
    toast.success("Ogledni primjer učitan");
  };

  const missing = tool.fields.filter((f) => !f.optional && !(values[f.name] || "").trim());

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/generate`, { tool: tool.id, fields: { ...values, ...common } });
      onResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri generiranju. Pokušajte ponovno.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tool-form" data-testid={`tool-form-${tool.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {tool.name}
          </h2>
          <p className="text-sm text-slate-500 mt-1">{tool.description}</p>
          {tool.note && (
            <a
              href={tool.noteUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`tool-note-${tool.id}`}
              className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline mt-1.5"
            >
              <BadgeCheck className="w-3.5 h-3.5" /> {tool.note}
            </a>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={loadPreset} data-testid={`load-preset-btn-${tool.id}`} className="shrink-0">
          <FileText className="w-4 h-4 mr-2" /> Učitaj ogledni primjer
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {tool.fields.map((f) => {
          if (f.type === "select") {
            return (
              <div key={f.name} className="space-y-1.5">
                <Label className="field-label">{f.label}</Label>
                <Select value={values[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${tool.id}-${f.name}`} className="bg-white">
                    <SelectValue placeholder="Odaberite..." />
                  </SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          }
          if (f.type === "input") {
            return (
              <div key={f.name} className="space-y-1.5 sm:col-span-2 lg:col-span-3">
                <Label className="field-label">{f.label}</Label>
                <Input
                  data-testid={`field-${tool.id}-${f.name}`}
                  className="bg-white"
                  placeholder={f.placeholder}
                  value={values[f.name] || ""}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              </div>
            );
          }
          return (
            <div key={f.name} className="space-y-1.5 sm:col-span-2 lg:col-span-3">
              <Label className="field-label">
                {f.label} {f.optional && <span className="text-slate-400 font-normal normal-case">(nije obavezno)</span>}
              </Label>
              <Textarea
                data-testid={`field-${tool.id}-${f.name}`}
                className="bg-white min-h-[110px]"
                placeholder={f.placeholder}
                value={values[f.name] || ""}
                onChange={(e) => set(f.name, e.target.value)}
              />
            </div>
          );
        })}
      </div>

      <div className="common-section">
        <p className="common-title">Zaglavlje i stil dokumenta <span className="text-slate-400 font-normal normal-case">(nije obavezno)</span></p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label className="field-label">Naziv škole</Label>
            <Input
              data-testid="common-skola"
              className="bg-white"
              placeholder="npr. Osnovna škola Ivana Gorana Kovačića"
              value={common.skola}
              onChange={(e) => setC("skola", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="field-label">Mjesto</Label>
            <Input
              data-testid="common-mjesto"
              className="bg-white"
              placeholder="npr. Zagreb"
              value={common.mjesto}
              onChange={(e) => setC("mjesto", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="field-label">Duljina dokumenta</Label>
            <Select value={common.duljina} onValueChange={(v) => setC("duljina", v)}>
              <SelectTrigger data-testid="common-duljina" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Kratko">Kratko i sažeto</SelectItem>
                <SelectItem value="Standardno">Standardno</SelectItem>
                <SelectItem value="Detaljno">Detaljno i opsežno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="field-label">Ton dokumenta</Label>
            <Select value={common.ton} onValueChange={(v) => setC("ton", v)}>
              <SelectTrigger data-testid="common-ton" className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Formalno-administrativni">Formalno-administrativni</SelectItem>
                <SelectItem value="Stručno-pedagoški">Stručno-pedagoški</SelectItem>
                <SelectItem value="Topao i podržavajući">Topao i podržavajući</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={loading || missing.length > 0}
        data-testid={`generate-btn-${tool.id}`}
        className="w-full mt-6 h-12 text-base font-semibold text-white"
        style={{ backgroundColor: tool.color }}
      >
        {loading ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generiram dokument...</>
        ) : (
          <><Wand2 className="w-5 h-5 mr-2" /> Izradi dokument</>
        )}
      </Button>
      {missing.length > 0 && (
        <p className="text-xs text-slate-400 mt-2 text-center">Ispunite sva obavezna polja za nastavak.</p>
      )}
    </div>
  );
};
